import type { LegalDoc } from '@/data/legal/types';

export const privacyPolicy: LegalDoc = {
  title: 'Privacy Policy',
  lastUpdated: '15 October 2026',
  sections: [
    {
      heading: '1. Who We Are',
      blocks: [
        {
          p: 'KOR38 is operated by Daniella Farrell, trading as KOR38, a sole trader based in London, United Kingdom.',
        },
        {
          p: 'KOR38 provides personalised event-supplier sourcing and introduction services, primarily across Greater London.',
        },
        {
          p: 'For the purposes of UK data protection law, Daniella Farrell trading as KOR38 is the data controller responsible for the personal information collected through this website and in connection with its services.',
        },
        { p: 'For privacy-related enquiries, please contact:' },
        { p: 'Email: hello@kor38.com' },
      ],
    },
    {
      heading: '2. Information We Collect',
      blocks: [
        { p: 'Depending on how you interact with KOR38, we may collect:' },
        {
          list: [
            'Your first and last name.',
            'Your email address and telephone number.',
            'Your business name, where applicable.',
            'Information about your event, including its type, date, location, budget and supplier requirements.',
            'Details submitted through customer enquiry and service request forms.',
            'Supplier business information, including services, experience, coverage areas, indicative pricing and relevant qualifications.',
            'Answers submitted through the KOR38 Supplier Insights Survey.',
            'Information provided when requesting our free Event Supplier Checklist.',
            'Your preferences regarding marketing communications.',
            'Correspondence and other information you voluntarily share with us.',
            'Limited technical information generated when you use our website, subject to the technologies in use.',
          ],
        },
        {
          p: 'We do not intentionally request sensitive personal information, such as medical information or details of religious beliefs. Please avoid including unnecessary sensitive information in enquiry forms.',
        },
      ],
    },
    {
      heading: '3. How We Use Your Information',
      blocks: [
        { p: 'We use personal information to:' },
        {
          list: [
            'Respond to enquiries and requests.',
            'Review event-supplier sourcing briefs.',
            'Identify and introduce potentially suitable suppliers.',
            'Review applications to join the KOR38 Supplier Network.',
            'Communicate with suppliers about relevant opportunities.',
            'Provide requested resources, including the Event Supplier Checklist.',
            'Analyse Supplier Insights Survey responses to better understand supplier needs and referral practices.',
            'Manage customer relationships, payments and business administration.',
            'Send marketing communications where you have specifically opted in.',
            'Maintain website security, investigate problems and comply with legal obligations.',
          ],
        },
        { p: 'KOR38 does not sell personal information to third parties.' },
      ],
    },
    {
      heading: '4. Our Lawful Bases for Processing',
      blocks: [
        { p: 'Under UK data protection law, we rely on the following lawful bases, as appropriate:' },
        {
          p: 'Contract and pre-contractual steps: Where processing is necessary to respond to a request for a service, prepare a quotation or deliver an agreed service.',
        },
        {
          p: 'Legitimate interests: Where reasonably necessary to respond to business enquiries, review supplier applications, manage supplier relationships, analyse research responses, administer our business and maintain website security, provided these interests are not overridden by individuals\u2019 rights.',
        },
        {
          p: 'Consent: Where you have chosen to receive optional marketing emails or where consent is otherwise legally required.',
        },
        {
          p: 'Legal obligation: Where information must be retained or processed to comply with applicable laws, including accounting and tax requirements.',
        },
        { p: 'Where consent is used, you may withdraw it at any time.' },
      ],
    },
    {
      heading: '5. Customer Enquiries and Supplier Introductions',
      blocks: [
        {
          p: 'When you submit an event enquiry, we use your information to understand your requirements and assess how KOR38 may be able to help.',
        },
        {
          p: 'Where appropriate, KOR38 may share relevant event requirements and contact information with potentially suitable suppliers for the purpose of arranging an introduction.',
        },
        {
          p: 'We will only share information reasonably necessary for that introduction and will not automatically distribute your enquiry to an unrestricted supplier network.',
        },
        {
          p: 'Suppliers operate independently of KOR38. Once you communicate or contract directly with a supplier, that supplier may act as a separate data controller under its own privacy policy.',
        },
      ],
    },
    {
      heading: '6. Supplier Applications',
      blocks: [
        {
          p: 'Information submitted through the supplier application form is used to assess potential suitability for the KOR38 Supplier Network and to communicate about applications and relevant business opportunities.',
        },
        { p: 'Submitting an application does not guarantee acceptance or inclusion in the network.' },
        {
          p: 'Supplier application information will not automatically be added to a marketing mailing list. Marketing consent is requested separately.',
        },
      ],
    },
    {
      heading: '7. Supplier Insights Survey',
      blocks: [
        {
          p: 'The KOR38 Supplier Insights Survey is designed to help us understand event suppliers\u2019 business needs, enquiry experiences and views on referral practices.',
        },
        { p: 'You may complete the survey without providing your name, business name or email address.' },
        { p: 'Survey responses may be analysed individually or in aggregate to inform business development.' },
        { p: 'If you voluntarily provide contact details, those details may be associated with your responses.' },
        {
          p: 'Participation in the survey does not automatically enrol you in the supplier network or subscribe you to marketing communications.',
        },
      ],
    },
    {
      heading: '8. Free Event Supplier Checklist',
      blocks: [
        {
          p: 'When you request the Event Supplier Checklist, we collect the information necessary to provide access to the resource.',
        },
        { p: 'You can receive the checklist whether or not you choose to receive marketing communications.' },
        {
          p: 'If you separately opt in to marketing communications, we may also send useful event-supplier guidance, business updates and relevant information about KOR38.',
        },
      ],
    },
    {
      heading: '9. Marketing Communications',
      blocks: [
        { p: 'Marketing emails are optional.' },
        { p: 'Where our forms request marketing consent, the checkbox is not selected by default.' },
        { p: 'We keep records of marketing preferences and use them to determine which communications may be sent.' },
        { p: 'You may unsubscribe at any time using the unsubscribe link in our marketing emails or by contacting us.' },
        {
          p: 'Withdrawing marketing consent will not prevent us from contacting you about an existing enquiry, application or service where necessary.',
        },
      ],
    },
    {
      heading: '10. How We Store and Protect Information',
      blocks: [
        {
          p: 'We take reasonable technical and organisational measures to protect personal information against unauthorised access, accidental loss, misuse or disclosure.',
        },
        {
          p: 'Information may be stored or processed using third-party services that support our website, forms, email communications, customer management and business operations. Access to personal information is limited to those who reasonably need it for legitimate business purposes.',
        },
        { p: 'No internet-based system can guarantee absolute security.' },
      ],
    },
    {
      heading: '11. Sharing Information with Third Parties',
      blocks: [
        { p: 'We may share personal information with:' },
        {
          list: [
            'Relevant event suppliers where necessary to facilitate requested introductions.',
            'Website hosting and form-processing providers.',
            'Email marketing and customer relationship management providers.',
            'Payment providers where paid services are purchased.',
            'Professional advisers or service providers where reasonably necessary.',
            'Public authorities where disclosure is required by law.',
          ],
        },
        {
          p: 'Third-party service providers are expected to handle information appropriately and in accordance with applicable data protection law.',
        },
        {
          p: 'We do not share contact details with suppliers for unrelated marketing purposes without an appropriate lawful basis.',
        },
      ],
    },
    {
      heading: '12. International Data Transfers',
      blocks: [
        { p: 'Some technology providers may process or store information outside the United Kingdom.' },
        {
          p: 'Where personal information is transferred internationally, we will use appropriate safeguards required by UK data protection law, where applicable.',
        },
      ],
    },
    {
      heading: '13. How Long We Keep Information',
      blocks: [
        {
          p: 'We retain personal information only for as long as reasonably necessary for the purpose for which it was collected, including applicable legal, accounting and business requirements.',
        },
        { p: 'Our intended retention arrangements are:' },
        {
          list: [
            'Customer enquiries that do not result in a service: Up to 24 months after the last meaningful interaction.',
            'Customer service and transaction records: Normally six years after the end of the relevant financial year, where required for accounting, tax or potential legal claims.',
            'Supplier applications and supplier network records: While the application or relationship remains active, with inactive records reviewed periodically.',
            'Supplier survey responses: Retained for research and business analysis, with identifying information removed when no longer required.',
            'Marketing contact information: Until you unsubscribe, withdraw consent or the information is no longer needed, subject to retaining a minimal suppression record where required.',
            'Technical and security records: Retained for an appropriate limited period according to their purpose.',
          ],
        },
        {
          p: 'You may request deletion of your information, subject to any applicable legal requirements or other lawful grounds for retention.',
        },
      ],
    },
    {
      heading: '14. Your Data Protection Rights',
      blocks: [
        { p: 'Under UK data protection law, you may have the right to:' },
        {
          list: [
            'Request access to personal information held about you.',
            'Request correction of inaccurate information.',
            'Request deletion of your information in certain circumstances.',
            'Request restriction of processing.',
            'Object to certain types of processing.',
            'Request transfer of information in applicable circumstances.',
            'Withdraw consent where processing relies on consent.',
          ],
        },
        { p: 'These rights are subject to legal conditions and exemptions.' },
        { p: 'To exercise your rights, please contact hello@kor38.com.' },
        {
          p: 'You also have the right to raise a complaint with the UK Information Commissioner\u2019s Office (ICO):',
        },
        { p: 'Website: ico.org.uk' },
        { p: 'We would welcome the opportunity to address any concerns directly.' },
      ],
    },
    {
      heading: '15. Cookies and Website Technologies',
      blocks: [
        {
          p: 'Our website may use cookies or similar technologies necessary for its operation, security and functionality.',
        },
        {
          p: 'If we introduce optional analytics, advertising or other non-essential technologies, we will provide appropriate information and obtain consent where required.',
        },
        {
          p: 'Further details will be provided through our cookie information or consent controls, as applicable.',
        },
      ],
    },
    {
      heading: '16. External Websites',
      blocks: [
        {
          p: 'Our website may contain links to external websites, including those operated by independent suppliers.',
        },
        {
          p: 'KOR38 is not responsible for the privacy practices of third-party websites. We recommend reviewing their privacy policies before submitting personal information.',
        },
      ],
    },
    {
      heading: '17. Children\u2019s Privacy',
      blocks: [
        {
          p: 'Our services are intended for adults and businesses arranging or supplying event services.',
        },
        { p: 'We do not knowingly collect personal information directly from children through our website.' },
      ],
    },
    {
      heading: '18. Changes to This Policy',
      blocks: [
        {
          p: 'We may update this Privacy Policy to reflect changes in our services, technology or legal requirements.',
        },
        { p: 'The latest version will be published on this website with an updated revision date.' },
      ],
    },
    {
      heading: '19. Contact Us',
      blocks: [
        {
          p: 'For questions about this Privacy Policy or how your personal information is handled, contact:',
        },
        { p: 'Daniella Farrell, trading as KOR38' },
        { p: 'London, United Kingdom' },
        { p: 'Email: hello@kor38.com' },
      ],
    },
  ],
};